import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BASE_URL } from '../config/api'; // Assuming BASE_URL is defined in your config/api.js

const EditPurchaseInvoice = () => {
    const { id } = useParams(); // Get the invoice ID from the URL
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        date: '',
        vno: '',
        ref_date: '',
        ref_no: '',
        party_id: '',
        location_id: '',
        bill_amount: ''
    });

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    // Fetch the invoice data when the component is mounted
    useEffect(() => {
        fetch(`${BASE_URL}purchaseinvoices/${id}`)
            .then((response) => response.json())
            .then((data) => {
                if (data.status === 'success') {
                    setFormData({
                        date: data.data.date.split('T')[0], // Format date as YYYY-MM-DD
                        vno: data.data.vno,
                        ref_date: data.data.ref_date.split('T')[0], // Format date as YYYY-MM-DD
                        ref_no: data.data.ref_no,
                        party_id: data.data.party_id,
                        location_id: data.data.location_id,
                        bill_amount: data.data.bill_amount
                    });
                    setLoading(false);
                } else {
                    setError('Failed to fetch invoice data.');
                }
            })
            .catch((error) => {
                setError('An error occurred while fetching the invoice data.');
                setLoading(false);
            });
    }, [id]);

    // Handle input field changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };

    // Handle form submission to update the invoice
    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.date || !formData.vno || !formData.ref_date || !formData.ref_no || !formData.party_id || !formData.location_id || !formData.bill_amount) {
            setError('Please fill all fields.');
            return;
        }

        const requestData = {
            date: formData.date,
            vno: formData.vno,
            ref_date: formData.ref_date,
            ref_no: formData.ref_no,
            party_id: parseInt(formData.party_id, 10),
            location_id: parseInt(formData.location_id, 10),
            bill_amount: parseFloat(formData.bill_amount)
        };

        // Send PUT request to update the purchase invoice
        fetch(`${BASE_URL}purchaseinvoices/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestData),
        })
            .then((response) => response.json())
            .then((data) => {
                if (data.status === 'success') {
                    // Redirect to the list of invoices after successful update
                    navigate('/PurchaseInvoicesList');
                } else {
                    setError('Failed to update purchase invoice.');
                }
            })
            .catch((error) => {
                console.error('Error updating purchase invoice:', error);
                setError('An error occurred while updating the purchase invoice.');
            });
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div id="main">
            <header className="mb-3">
                <a href="#" className="burger-btn d-block d-xl-none">
                    <i className="bi bi-justify fs-3"></i>
                </a>
            </header>

            <div className="page-heading">
                <div className="page-title">
                    <div className="row">
                        <div className="col-12 col-md-6 order-md-1 order-last">
                            <h3>Edit Purchase Invoice</h3>
                        </div>
                        <div className="col-12 col-md-6 order-md-2 order-first">
                            <nav aria-label="breadcrumb" className="breadcrumb-header float-start float-lg-end">
                                <ol className="breadcrumb"></ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            {/* Form for editing a purchase invoice */}
            <section className="section">
                <div className="row">
                    <div className="col-12 col-md-12">
                        <div className="card">
                            <div className="card-header">
                                <h4 className="card-title">Edit Purchase Invoice Form</h4>
                            </div>
                            <div className="card-body">
                                {error && <div className="alert alert-danger">{error}</div>}

                                <form onSubmit={handleSubmit}>
                                    <div className="mb-3">
                                        <label htmlFor="date" className="form-label">Invoice Date</label>
                                        <input
                                            type="date"
                                            id="date"
                                            className="form-control"
                                            name="date"
                                            value={formData.date}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="vno" className="form-label">Invoice No</label>
                                        <input
                                            type="text"
                                            id="vno"
                                            className="form-control"
                                            name="vno"
                                            value={formData.vno}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="ref_date" className="form-label">Reference Date</label>
                                        <input
                                            type="date"
                                            id="ref_date"
                                            className="form-control"
                                            name="ref_date"
                                            value={formData.ref_date}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="ref_no" className="form-label">Reference No</label>
                                        <input
                                            type="text"
                                            id="ref_no"
                                            className="form-control"
                                            name="ref_no"
                                            value={formData.ref_no}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="party_id" className="form-label">Party ID</label>
                                        <input
                                            type="number"
                                            id="party_id"
                                            className="form-control"
                                            name="party_id"
                                            value={formData.party_id}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="location_id" className="form-label">Location ID</label>
                                        <input
                                            type="number"
                                            id="location_id"
                                            className="form-control"
                                            name="location_id"
                                            value={formData.location_id}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="bill_amount" className="form-label">Bill Amount</label>
                                        <input
                                            type="number"
                                            id="bill_amount"
                                            className="form-control"
                                            name="bill_amount"
                                            value={formData.bill_amount}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>

                                    <div className="text-end">
                                        <button type="submit" className="btn btn-primary">Update Invoice</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default EditPurchaseInvoice;
